import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import EnumService from "../../../Services/Shared/Enum.service";
export const DAYS_OF_WEEK = {
    sun: 0,
    mon: 1,
    tues: 2,
    wed: 3,
    thur: 4,
    fri: 5,
    sat: 6,
};
export const getDayOfWeek = EnumService.createValueFinder(DAYS_OF_WEEK);
const HEADER_HEIGHT = 50;
const TIME_WIDTH = 70;
const getTimeString = (time) => {
    const isAm = time < 12;
    const hour = Math.floor(time) % 12 || 12;
    const minute = (time - Math.floor(time)) * 60;
    const minuteString = minute < 10 ? "0" + Math.round(minute) : Math.round(minute).toString();
    return `${hour}:${minuteString} ${isAm ? "AM" : "PM"}`;
};
const getPosition = (val, startTime, endTime, height, timeStep) => ((val - startTime) / (endTime - startTime + timeStep)) * height + 1; // Plus 1 for border.
const DayHeader = ({ day }) => (_jsx("div", { className: "d-flex align-items-center justify-content-center font-weight-bold", style: { height: HEADER_HEIGHT + "px" }, children: day }));
const WeekendHeader = ({ day }) => (_jsx("div", { className: "d-flex align-items-center justify-content-center text-muted", style: { height: HEADER_HEIGHT + "px" }, children: day }));
const Day = ({ blocks, startTime, endTime, dayName, height, timeStep, }) => (_jsxs("div", { className: "d-flex flex-column flex-fill", children: [_jsx(DayHeader, { day: dayName }), _jsx("div", { className: "border border-top-0 flex-grow-1 position-relative", children: blocks &&
                blocks.map((block, index) => (_jsx("div", { style: {
                        position: "absolute",
                        left: "5px",
                        right: "5px",
                        overflow: "hidden",
                        top: getPosition(block.startTime24, startTime, endTime, height, timeStep) + "px",
                        bottom: height -
                            getPosition(block.endTime24, startTime, endTime, height, timeStep) +
                            "px",
                    }, children: block.content }, index))) })] }));
const defaultConfig = {
    startTime24: 8.5,
    endTime24: 20,
    height: 800,
    showTimes: true,
    showGrid: true,
    showWeekends: true,
    timeMajorInterval: 2,
    timeMinorInterval: 0.5,
};
const Scheduler = ({ blocks, config }) => {
    const safeConfig = Object.assign(Object.assign({}, defaultConfig), config);
    // @ts-expect-error - TS2540 - Cannot assign to 'startTime24' because it is a read-only property.
    safeConfig.startTime24 = Math.floor(safeConfig.startTime24);
    // @ts-expect-error - TS2540 - Cannot assign to 'endTime24' because it is a read-only property.
    safeConfig.endTime24 = Math.ceil(safeConfig.endTime24);
    // Computed values from props
    const contentHeight = safeConfig.height - HEADER_HEIGHT;
    /**
     * Time step is unit in terms of hours the scheduler will be displayed in.
     */
    const timeStep = safeConfig.timeMajorInterval * safeConfig.timeMinorInterval;
    const timeWidth = safeConfig.showTimes ? TIME_WIDTH : 0;
    const times = React.useMemo(() => {
        const timesTemp = [];
        for (let time = safeConfig.startTime24; time <= safeConfig.endTime24; time += timeStep) {
            timesTemp.push(Math.round(time * 100) / 100);
        }
        return timesTemp;
    }, [safeConfig.endTime24, safeConfig.startTime24, timeStep]);
    const isMajorTime = (time) => (time - safeConfig.startTime24) % safeConfig.timeMajorInterval === 0;
    return (_jsx("div", { className: "d-flex flex-column", children: _jsxs("div", { className: "w-100", style: { position: "relative", height: safeConfig.height + "px" }, children: [safeConfig.showTimes && (_jsxs("div", { className: "d-flex flex-column", style: { width: timeWidth + "px", height: "100%" }, children: [_jsx("div", { style: { height: HEADER_HEIGHT + "px" } }), _jsx("div", { className: "d-flex flex-column flex-grow-1 border-right", children: times.map((time) => (_jsx("div", { className: "flex-grow-1 d-flex align-items-top justify-content-center" +
                                    (safeConfig.showGrid ? " border-top" : "") +
                                    (isMajorTime(time) ? " border-secondary" : " text-muted"), children: getTimeString(time) }, time))) })] })), safeConfig.showGrid && (_jsx("div", { className: "d-flex flex-column", style: {
                        position: "absolute",
                        bottom: 0,
                        left: timeWidth + "px",
                        top: HEADER_HEIGHT + "px",
                        right: "0px",
                        zIndex: 3,
                    }, children: times.map((time) => (_jsx("div", { className: "flex-grow-1 border-top w-100 " +
                            (isMajorTime(time) ? " border-secondary" : "") }, time))) })), _jsxs("div", { className: "h-100 d-flex", style: {
                        position: "absolute",
                        left: timeWidth + "px",
                        top: 0,
                        right: 0,
                        zIndex: 4,
                    }, children: [safeConfig.showWeekends && (_jsxs("div", { className: "d-flex flex-column flex-fill", children: [_jsx(WeekendHeader, { day: "SUN" }), _jsx("div", { className: "flex-grow-1 bg-light", style: { opacity: 0.6 } })] })), _jsx(Day, { blocks: blocks.filter((block) => block.days.has("mon")), startTime: safeConfig.startTime24, endTime: safeConfig.endTime24, dayName: "MON", height: contentHeight, timeStep: safeConfig.timeMajorInterval * safeConfig.timeMinorInterval }), _jsx(Day, { blocks: blocks.filter((block) => block.days.has("tues")), startTime: safeConfig.startTime24, endTime: safeConfig.endTime24, dayName: "TUE", height: contentHeight, timeStep: safeConfig.timeMajorInterval * safeConfig.timeMinorInterval }), _jsx(Day, { blocks: blocks.filter((block) => block.days.has("wed")), startTime: safeConfig.startTime24, endTime: safeConfig.endTime24, dayName: "WED", height: contentHeight, timeStep: safeConfig.timeMajorInterval * safeConfig.timeMinorInterval }), _jsx(Day, { blocks: blocks.filter((block) => block.days.has("thur")), startTime: safeConfig.startTime24, endTime: safeConfig.endTime24, dayName: "THU", height: contentHeight, timeStep: safeConfig.timeMajorInterval * safeConfig.timeMinorInterval }), _jsx(Day, { blocks: blocks.filter((block) => block.days.has("fri")), startTime: safeConfig.startTime24, endTime: safeConfig.endTime24, dayName: "FRI", height: contentHeight, timeStep: safeConfig.timeMajorInterval * safeConfig.timeMinorInterval }), safeConfig.showWeekends && (_jsxs("div", { className: "d-flex flex-column flex-fill", children: [_jsx(WeekendHeader, { day: "SAT" }), _jsx("div", { className: "flex-grow-1 bg-light", style: { opacity: 0.6 } })] }))] })] }) }));
};
export default Scheduler;
